import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, SxProps, Theme, Typography } from '@mui/material';

type RankChipsProps<T extends string> = Readonly<{
  id: string;
  label: string;
  order: readonly T[];
  optionLabel: (option: T) => string;
  onReorder: (order: readonly T[]) => void;
}>;

export const RankChips = <T extends string>({
  id,
  label,
  order,
  optionLabel,
  onReorder,
}: RankChipsProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    onReorder(arrayMove([...order], order.indexOf(active.id as T), order.indexOf(over.id as T)));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={[...order]} strategy={verticalListSortingStrategy}>
        <Box component="ol" sx={listSx} aria-label={label}>
          {order.map((option, index) => (
            <SortableRankChip
              key={option}
              id={`${id}-${option}`}
              option={option}
              rank={index + 1}
              label={optionLabel(option)}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

type SortableRankChipProps = Readonly<{
  id: string;
  option: string;
  rank: number;
  label: string;
}>;

const SortableRankChip = ({ id, option, rank, label }: SortableRankChipProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option,
  });

  return (
    <Box
      component="li"
      ref={setNodeRef}
      id={id}
      {...attributes}
      {...listeners}
      sx={itemSx(isDragging)}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Typography component="span" variant="text-xs-medium" sx={rankSx}>
        {rank}
      </Typography>
      <Typography component="span" variant="text-sm-medium" sx={labelSx}>
        {label}
      </Typography>
    </Box>
  );
};

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

const itemSx =
  (isDragging: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-md'],
    paddingInline: backrs.spacing['spacing-xl'],
    paddingBlock: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-lg'],
    border: `1px solid ${backrs.colors[isDragging ? 'Brand/500' : 'Colors/Border/border-secondary']}`,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    cursor: isDragging ? 'grabbing' : 'pointer',
    touchAction: 'none',
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
    '&:hover': { borderColor: backrs.colors['Brand/300'] },
    '&:focus-visible': {
      outline: `2px solid ${backrs.colors['Brand/500']}`,
      outlineOffset: '2px',
    },
  });

const rankSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: backrs.spacing['spacing-xl'],
  height: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xs'],
  border: `1px solid ${backrs.colors['Colors/Text/text-secondary']}`,
  color: backrs.colors['Colors/Text/text-secondary'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
